import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "form",
    "accountType",
    "store",
    "perfil",
    "accounts",
    "validateBtn",
    "saveBtn",
    "raffleBtn",
    "report",
    "errorSummary",
    "raffleDigits",
    "raffleDigitsContainer",
    "raffleImage",
    "raffleImageContainer"
  ]

  accountsAreValid = false

  connect() {
    this.disableSave()
    this.checkRaffleButton()
    this.checkValidateButton()
    this.initializeSelect2()
  }

  initializeSelect2() {
    const $ = window.jQuery
    if ($ && this.hasAccountTypeTarget && this.hasStoreTarget) {
      $(this.accountTypeTarget).select2({ width: '100%' });
      $(this.storeTarget).select2({ width: '100%', minimumResultsForSearch: Infinity });

      $(this.accountTypeTarget).on('change', () => {
        this.onFieldInputChange()
        this.checkRaffleButton()
      })

      $(this.storeTarget).on('change', () => {
        this.onFieldInputChange()
      })
    }
  }

  // --- FORM VALIDATION ---
  get checkFormValidity() {
    let isValid = true
    let missingFields = []
    const $ = window.jQuery
    const isRaffle = this.accountTypeTarget.value === '37'

    const requiredTargets = [
      { target: "accountType", label: "Tipo de cuenta" },
      { target: "store", label: "Almacén" },
      { target: "perfil", label: "Perfil" },
      { target: "accounts", label: "Cuentas" }
    ]

    // Add image requirement for raffles
    if (isRaffle) {
      requiredTargets.push({ target: "raffleImage", label: "Imagen de la rifa" })
    }

    requiredTargets.forEach(item => {
      const element = this[`${item.target}Target`]
      if (!element) return
      
      const value = element.value.trim()
      const $el = $ ? $(element) : null

      if (value === "") {
        isValid = false
        missingFields.push(item.label)
        this.markInvalid(element, $el)
      } else {
        this.markValid(element, $el)
      }
    })

    this.renderErrorSummary(missingFields)
    return isValid
  }

  markInvalid(element, $el) {
    if ($el && $el.hasClass('select2-hidden-accessible')) {
      $el.addClass('is-invalid')
      $el.next('.select2-container').find('.select2-selection').css('border-color', '#dc3545')
    } else {
      element.classList.add('is-invalid')
    }
  }

  markValid(element, $el) {
    if ($el && $el.hasClass('select2-hidden-accessible')) {
      $el.removeClass('is-invalid')
      $el.next('.select2-container').find('.select2-selection').css('border-color', '')
    } else {
      element.classList.remove('is-invalid')
    }
  }

  renderErrorSummary(missingFields) {
    if (missingFields.length > 0) {
      this.errorSummaryTarget.style.display = 'block'
      const list = document.getElementById('error_list')
      if (list) {
        list.innerHTML = missingFields.map(field => `<li>El campo <strong>${field}</strong> es obligatorio.</li>`).join('')
      }
    } else {
      this.errorSummaryTarget.style.display = 'none'
    }
  }
  // --- END VALIDATION ---

  disableSave() {
    this.saveBtnTarget.disabled = true
  }

  checkValidateButton() {
    this.validateBtnTarget.disabled = this.accountsTarget.value.trim().length < 5
  }

  // Prevents double submission by blocking the button when pressed
  blockSubmit() {
    this.saveBtnTarget.disabled = true
    this.saveBtnTarget.innerHTML = '<i class="fa fa-spinner fa-spin mr-1"></i> GUARDANDO...'
  }

  checkRaffleButton() {
    const isRaffle = this.accountTypeTarget.value === '37'

    // Show/hide raffle button
    this.raffleBtnTarget.style.display = isRaffle ? 'block' : 'none'

    // Show/hide digits selector
    if (this.hasRaffleDigitsContainerTarget) {
      this.raffleDigitsContainerTarget.style.display = isRaffle ? 'block' : 'none'
    }

    // Show/hide image upload field
    if (this.hasRaffleImageContainerTarget) {
      this.raffleImageContainerTarget.style.display = isRaffle ? 'block' : 'none'
    }

    if (isRaffle && (this.perfilTarget.value === '' || this.perfilTarget.value === 'Nombre del perfil')) {
      this.perfilTarget.value = 'Rifa'
    }
  }

  onFieldInputChange() {
    this.updateSaveButtonState()
  }

  onAccountsInputChange() {
    this.accountsAreValid = false
    this.reportTarget.innerHTML = 
      '<div class="alert alert-warning py-2 small mt-2">Cuentas modificadas. <strong>Re-validar</strong>.</div>'
    this.checkValidateButton()
    this.updateSaveButtonState()
  }

  updateSaveButtonState() {
    const formOk = this.checkFormValidity
    this.saveBtnTarget.disabled = !(this.accountsAreValid && formOk)
  }

  generateRaffle() {
    let raffleContent = ""
    const digits = this.hasRaffleDigitsTarget ? parseInt(this.raffleDigitsTarget.value) : 2
    const limit = digits === 3 ? 999 : 99
    
    for (let i = 0; i <= limit; i++) {
      const num = i.toString().padStart(digits, '0')
      raffleContent += `${num}:${num}\n`
    }
    this.accountsTarget.value = raffleContent.trim()

    if (this.perfilTarget.value === "") {
      this.perfilTarget.value = "Rifa"
    }

    this.reportTarget.innerHTML = 
      '<div class="alert alert-info py-2 small mt-2">Rifa generada. Pulse <strong>Validar</strong>.</div>'
    
    this.accountsAreValid = false
    this.checkValidateButton()
    this.updateSaveButtonState()
  }

  validate() {
    const $ = window.jQuery
    const accounts = this.accountsTarget.value
    
    if (!accounts) {
      this.checkFormValidity
      return
    }

    this.validateBtnTarget.disabled = true
    this.reportTarget.innerHTML = '<p class="small text-muted"><i class="fa fa-spinner fa-spin"></i> Procesando...</p>'

    $.ajax({
      url: '/cuentas_digitales/validar',
      method: 'POST',
      data: {
        cuentas: accounts,
        tipo_cuenta_digital_id: this.accountTypeTarget.value,
        authenticity_token: document.querySelector('meta[name="csrf-token"]').content
      },
      success: (response) => {
        this.validateBtnTarget.disabled = false

        if (response.raffles_limit_reached) {
          this.accountsAreValid = false
          this.reportTarget.innerHTML = `
            <div class="alert alert-danger py-3 mt-2">
              <h6 class="alert-heading"><i class="fa fa-times-circle"></i> Límite de Rifas alcanzado</h6>
              <p class="small mb-0">No se pueden crear más de <strong>2 rifas activas</strong> simultáneamente.</p>
              <p class="small mb-0 mt-2">Actualmente hay <strong>${response.open_raffles_count}</strong> rifas abiertas. Cierre una para continuar.</p>
            </div>
          `
        } else {
          this.accountsAreValid = response.invalidas === 0 && response.repetidas === 0
          const formOk = this.checkFormValidity
          const alertClass = this.accountsAreValid ? 'alert-success' : 'alert-danger'
          let feedback = this.accountsAreValid ? 'Cuentas validadas correctamente.' : 'Hay errores en las cuentas.'

          if (!formOk || !this.accountsAreValid) {
          this.reportTarget.innerHTML = `
            <div class="alert ${alertClass} py-2 mt-2">
              <strong class="small">Resultado validación:</strong><br>
              <div class="small">
                - Válidas: ${response.validas}<br>
                - Inválidas: ${response.invalidas}<br>
                - Repetidas: ${response.repetidas}
              </div>
              <p class="mb-0 mt-1 small font-weight-bold">${feedback}</p>
            </div>
          `
        } else {
           this.reportTarget.innerHTML = `
            <div class="alert alert-success py-2 mt-2">
              <i class="fa fa-check-circle"></i> Todo correcto. Ya puede guardar.
            </div>
          `
          }
        }

        this.updateSaveButtonState()
      },
      error: () => {
        this.validateBtnTarget.disabled = false
        this.reportTarget.innerHTML = 
          '<div class="alert alert-danger py-2 mt-2 small">Error de conexión.</div>'
      }
    })
  }
}
